import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout/DashboardLayout";
import { getApiUrl } from "../../config/apiConfig";
import "./CreateBlog.css";

const CreateBlog = () => {

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    content: "",
    category: "",
    tags: "",
    youtubeLink: "",
    //facebookLink: "", // Added
    featuredImage: null,
    imagePreview: null,
    isPublished: false,
    allowComments: true,
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Category State
  const [categories, setCategories] = useState([]);

  // ===============================
  // Load Categories on Page Load
  // ===============================
  useEffect(() => {

    fetchCategories();

  }, []);

  const fetchCategories = async () => {

    try {

      const response = await fetch(
        getApiUrl("/Master/API/GetBlogcategoryMaster"),
        {
          method: "POST",
          headers: {
            "Authorization":
              `Bearer ${localStorage.getItem("authToken")}`
          }
        }
      );

      const data = await response.json();

      console.log("Category Data:", data);

      // Filter Active Categories
      setCategories(
        data.filter(x => x.isActive === true)
      );

    } catch (error) {

      console.error("Category Load Error:", error);

    }

  };

  // ===============================
  // Handle Input Change
  // ===============================
  const handleInputChange = (e) => {

    const { name, value, type, checked } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : value
    }));

    if (errors[name]) {

      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));

    }

  };

  // ===============================
  // Image Upload
  // ===============================
  const handleImageUpload = (e) => {

    const file = e.target.files[0];

    if (file) {

      if (!file.type.startsWith("image/")) {

        setErrors(prev => ({
          ...prev,
          featuredImage:
            "Please select a valid image file"
        }));

        return;
      }

      if (file.size > 5 * 1024 * 1024) {

        setErrors(prev => ({
          ...prev,
          featuredImage:
            "Image size should be less than 5MB"
        }));

        return;
      }

      setFormData(prev => ({
        ...prev,
        featuredImage: file,
        imagePreview:
          URL.createObjectURL(file)
      }));

    }

  };

  // ===============================
  // Validate Form
  // ===============================
  const validateForm = () => {

    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Blog title is required";
    }

    if (!formData.excerpt.trim()) {
      newErrors.excerpt = "Blog summary is required";
    }

    if (!formData.content.trim()) {
      newErrors.content = "Blog content is required";
    }

    if (!formData.category) {
      newErrors.category = "Select category";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;

  };

  // ===============================
  // Submit Blog API
  // ===============================
  const handleSubmit = async (e) => {

    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {

      const submitData = new FormData();

      // Match ASP.NET Model

      submitData.append(
        "Title",
        formData.title
      );

      submitData.append(
        "BlogCategoryId",
        formData.category
      );

      submitData.append(
        "Summary",
        formData.excerpt
      );

      submitData.append(
        "BlogContent",
        formData.content
      );

      submitData.append(
        "BlogYoutubeUrl",
        formData.youtubeLink || ""
      );

      /* submitData.append(
        "BlogFacebookUrl",
        formData.facebookLink || ""
      ); */

      submitData.append(
        "BlogTags",
        formData.tags || ""
      );

      submitData.append(
        "IsPublished",
        formData.isPublished
      );

      submitData.append(
        "IsAlloweComments",
        formData.allowComments
      );

      submitData.append(
        "IsBlogsaveDraft",
        false
      );

      // File Upload
      if (formData.featuredImage) {

        submitData.append(
          "BlogDocument",
          formData.featuredImage
        );

      }

      const response = await fetch(
        getApiUrl("/Trans/API/addBlogs"),
        {
          method: "POST",
          headers: {
            "Authorization":
              `Bearer ${localStorage.getItem("authToken")}`
          },
          body: submitData
        }
      );

      const result = await response.json();

      console.log("Submit Result:", result);

      if (response.ok) {

        alert(result.message);

        // Navigate to Blogs page
        navigate("/dashboard/blogs");

      } else {

        alert("Failed to create blog");

      }

    } catch (error) {

      console.error("Submit Error:", error);

      alert("Network error");

    } finally {

      setLoading(false);

    }

  };

  // ===============================
  // Remove Image
  // ===============================
  const removeImage = () => {

    setFormData(prev => ({
      ...prev,
      featuredImage: null,
      imagePreview: null
    }));

  };

  return (

    <DashboardLayout>

      <div className="create-blog-page">

        <div className="page-header">

          <h2>Create New Blog</h2>

          <p>
            Share your knowledge and insights
          </p>

        </div>

        <form
          onSubmit={handleSubmit}
          className="blog-form"
        >

          {/* Title */}

          <div className="form-group">

            <label>Blog Title *</label>

            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
            />

          </div>

          {/* Category */}

          <div className="form-group">

            <label>Category *</label>

            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
            >

              <option value="">
                Select Category
              </option>

              {categories.map(cat => (

                <option
                  key={cat.id}
                  value={cat.id}
                >
                  {cat.name}
                </option>

              ))}

            </select>

          </div>

          {/* Summary */}

          <div className="form-group">

            <label>Summary *</label>

            <textarea
              name="excerpt"
              value={formData.excerpt}
              onChange={handleInputChange}
            />

          </div>

          {/* Content */}

          <div className="form-group">

            <label>Content *</label>

            <textarea
              name="content"
              value={formData.content}
              onChange={handleInputChange}
            />

          </div>

          {/* Image */}

          <div className="form-group">

            <label>Image</label>

            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
            />

            {formData.imagePreview && (

              <img
                src={formData.imagePreview}
                alt="Preview"
                width="150"
              />

            )}

          </div>

          {/* Youtube */}

          <div className="form-group">

            <label>YouTube Link</label>

            <input
              type="url"
              name="youtubeLink"
              value={formData.youtubeLink}
              onChange={handleInputChange}
            />

          </div>

          {/* Facebook */}

          {/* <div className="form-group">

            <label>Facebook Link</label>

            <input
              type="url"
              name="facebookLink"
              value={formData.facebookLink}
              onChange={handleInputChange}
            />

          </div> */}

          {/* Tags */}

          <div className="form-group">

            <label>Tags</label>

            <input
              type="text"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
            />

          </div>

          {/* Checkboxes */}

          <div>

            <label>

              <input
                type="checkbox"
                name="isPublished"
                checked={formData.isPublished}
                onChange={handleInputChange}
              />

              Publish

            </label>

          </div>

          <div>

            <label>

              <input
                type="checkbox"
                name="allowComments"
                checked={formData.allowComments}
                onChange={handleInputChange}
              />

              Allow Comments

            </label>

          </div>

          {/* Submit */}

          <button
            type="submit"
            disabled={loading}
          >

            {loading
              ? "Saving..."
              : "Create Blog"}

          </button>

        </form>

      </div>

    </DashboardLayout>

  );

};

export default CreateBlog;